import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { monthStr, monthLabel } from "@/lib/format";
import { SectionTitle } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Settings as SettingsIcon, Plus, Trash2, Users, SlidersHorizontal, ListChecks, Scale, Lock, Copy } from "lucide-react";

const ROLES = ["telecaller", "manager", "owner"];
const btnGold = "bg-[#C9A24B] hover:bg-[#b8923f] text-[#0b1120]";

function EmployeesTab() {
  const { employees, refreshEmployees } = useApp();
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const blank = { name: "", role: "telecaller", team: "", phone: "", joining_date: "", active: true };
  const [f, setF] = useState(blank);

  const save = async () => {
    if (!f.name) { toast.error("Name required"); return; }
    if (edit) await api.updateEmployee(edit, f); else await api.createEmployee(f);
    await refreshEmployees(); setOpen(false); setEdit(null); setF(blank); toast.success("Saved");
  };
  const toggleActive = async (e) => { await api.updateEmployee(e.id, { active: !e.active }); refreshEmployees(); };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEdit(null); setF(blank); } }}>
          <DialogTrigger asChild><Button className={`rounded-lg ${btnGold}`} data-testid="add-employee-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Employee</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{edit ? "Edit" : "Add"} Employee</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="emp-name" />
              <div className="grid grid-cols-2 gap-3">
                <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                  <SelectTrigger data-testid="emp-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Team" value={f.team} onChange={(e) => setF({ ...f, team: e.target.value })} data-testid="emp-team" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="emp-phone" />
                <Input type="date" value={f.joining_date} onChange={(e) => setF({ ...f, joining_date: e.target.value })} data-testid="emp-joining" />
              </div>
            </div>
            <DialogFooter><Button onClick={save} className={btnGold} data-testid="save-employee-btn">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="surface overflow-hidden">
        <table className="w-full text-sm" data-testid="employees-table">
          <thead><tr className="text-left text-slate-500 text-xs uppercase tracking-wide border-b border-white/10 bg-white/5">
            <th className="py-2.5 px-4">Name</th><th className="py-2.5 px-4">Role</th><th className="py-2.5 px-4">Team</th>
            <th className="py-2.5 px-4">Phone</th><th className="py-2.5 px-4 text-center">Active</th><th className="py-2.5 px-4"></th>
          </tr></thead>
          <tbody>
            {employees.map((e) => (
              <tr key={e.id} className="border-b border-white/5" data-testid={`emp-row-${e.id}`}>
                <td className="py-2.5 px-4 font-semibold text-slate-100">{e.name}</td>
                <td className="py-2.5 px-4 capitalize text-slate-400">{e.role}</td>
                <td className="py-2.5 px-4 text-slate-400">{e.team || "—"}</td>
                <td className="py-2.5 px-4 text-slate-400 font-mono">{e.phone || "—"}</td>
                <td className="py-2.5 px-4 text-center"><Switch checked={e.active} onCheckedChange={() => toggleActive(e)} data-testid={`emp-active-${e.id}`} /></td>
                <td className="py-2.5 px-4 text-right">
                  <Button variant="ghost" size="sm" className="text-slate-300" data-testid={`edit-emp-${e.id}`} onClick={() => { setEdit(e.id); setF({ name: e.name, role: e.role, team: e.team, phone: e.phone, joining_date: e.joining_date, active: e.active }); setOpen(true); }}>Edit</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TargetsTab() {
  const { telecallers } = useApp();
  const [month, setMonth] = useState(monthStr());
  const [targets, setTargets] = useState({});
  const load = () => api.getTargets(month).then((rows) => { const map = {}; rows.forEach((r) => { map[r.employee_id] = r; }); setTargets(map); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);
  const setVal = (empId, k, v) => setTargets((t) => ({ ...t, [empId]: { ...t[empId], employee_id: empId, month, [k]: v } }));
  const save = async (empId) => { await api.upsertTarget({ ...targets[empId], employee_id: empId, month }); toast.success("Target saved"); };
  const copyPrev = async () => {
    const [y, m] = month.split("-").map(Number);
    const pm = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
    await api.copyTargets(pm, month); load(); toast.success(`Copied from ${monthLabel(pm)}`);
  };
  const FIELDS = [["calls", "Calls"], ["visits", "Visits"], ["leads", "Leads"], ["revenue", "Revenue"], ["treatments", "Treatments"], ["connect_rate", "Connect %"], ["treatment_conversion", "Conv %"], ["working_days", "Work Days"]];
  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 rounded-lg w-40" data-testid="targets-month" />
        <Button variant="outline" size="sm" className="rounded-lg" onClick={copyPrev} data-testid="copy-targets-btn"><Copy className="h-4 w-4 mr-1.5" /> Copy from last month</Button>
      </div>
      <div className="surface p-5 overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm" data-testid="targets-table">
          <thead><tr className="text-left text-slate-500 text-xs uppercase tracking-wide border-b border-white/10">
            <th className="py-2 pr-3">Employee</th>{FIELDS.map(([k, l]) => <th key={k} className="py-2 px-2">{l}</th>)}<th className="py-2 pl-2"></th>
          </tr></thead>
          <tbody>
            {telecallers.map((e) => (
              <tr key={e.id} className="border-b border-white/5" data-testid={`target-row-${e.id}`}>
                <td className="py-2 pr-3 font-semibold text-slate-200 whitespace-nowrap">{e.name}</td>
                {FIELDS.map(([k]) => (
                  <td key={k} className="py-2 px-2"><Input type="number" value={targets[e.id]?.[k] ?? ""} onChange={(ev) => setVal(e.id, k, parseInt(ev.target.value || "0", 10))} className="h-8 w-20 rounded-lg font-mono" data-testid={`target-${e.id}-${k}`} /></td>
                ))}
                <td className="py-2 pl-2"><Button size="sm" className={`rounded-lg h-8 ${btnGold}`} onClick={() => save(e.id)} data-testid={`save-target-${e.id}`}>Save</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CustomKpiTab() {
  const [kpis, setKpis] = useState([]);
  const [f, setF] = useState({ name: "", type: "number", frequency: "daily", target: 0, weight: 0, mandatory: false, enabled: true });
  const load = () => api.listKpis().then(setKpis);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const add = async () => { if (!f.name) { toast.error("Name required"); return; } await api.createKpi(f); setF({ name: "", type: "number", frequency: "daily", target: 0, weight: 0, mandatory: false, enabled: true }); load(); toast.success("KPI added"); };
  const toggle = async (k) => { await api.updateKpi(k.id, { enabled: !k.enabled }); load(); };
  const del = async (id) => { await api.deleteKpi(id); load(); };
  return (
    <div className="space-y-4">
      <div className="surface p-5 grid grid-cols-1 sm:grid-cols-7 gap-2">
        <Input placeholder="KPI name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-lg sm:col-span-2" data-testid="kpi-name" />
        <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger className="rounded-lg" data-testid="kpi-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="number">Number</SelectItem><SelectItem value="percent">Percent</SelectItem><SelectItem value="currency">Currency</SelectItem></SelectContent></Select>
        <Select value={f.frequency} onValueChange={(v) => setF({ ...f, frequency: v })}><SelectTrigger className="rounded-lg" data-testid="kpi-freq"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent></Select>
        <Input type="number" placeholder="Target" value={f.target || ""} onChange={(e) => setF({ ...f, target: parseInt(e.target.value || "0", 10) })} className="rounded-lg" data-testid="kpi-target" />
        <Input type="number" placeholder="Weight" value={f.weight || ""} onChange={(e) => setF({ ...f, weight: parseInt(e.target.value || "0", 10) })} className="rounded-lg" data-testid="kpi-weight" />
        <Button onClick={add} className={`rounded-lg ${btnGold}`} data-testid="add-kpi-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>
      <div className="space-y-2">
        {kpis.length === 0 && <div className="text-sm text-slate-500 text-center py-6">No custom KPIs yet.</div>}
        {kpis.map((k) => (
          <div key={k.id} className="surface p-4 flex items-center gap-3" data-testid={`kpi-item-${k.id}`}>
            <div className="flex-1"><div className="font-semibold text-slate-100 text-sm">{k.name}</div><div className="text-xs text-slate-500 capitalize">{k.type} · {k.frequency} · target {k.target} · weight {k.weight}</div></div>
            <Switch checked={k.enabled} onCheckedChange={() => toggle(k)} data-testid={`kpi-toggle-${k.id}`} />
            <button onClick={() => del(k.id)} className="text-rose-400 hover:text-rose-300" data-testid={`kpi-del-${k.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ListEditor({ label, items, onChange, testid }) {
  const [val, setVal] = useState("");
  return (
    <div className="surface p-5">
      <div className="font-heading text-lg font-light text-white mb-3">{label}</div>
      <div className="flex flex-wrap gap-2 mb-3">
        {items.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 bg-white/5 text-slate-200 text-sm px-2.5 py-1 rounded-lg" data-testid={`${testid}-item-${i}`}>
            {it}<button onClick={() => onChange(items.filter((_, x) => x !== i))} className="text-slate-500 hover:text-rose-400" data-testid={`${testid}-remove-${i}`}><Trash2 className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder="Add new…" className="rounded-lg h-9" data-testid={`${testid}-input`} onKeyDown={(e) => { if (e.key === "Enter" && val) { onChange([...items, val]); setVal(""); } }} />
        <Button variant="outline" className="rounded-lg h-9" onClick={() => { if (val) { onChange([...items, val]); setVal(""); } }} data-testid={`${testid}-add`}><Plus className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function ScoringTab() {
  const { settings, setSettings } = useApp();
  const [weights, setWeights] = useState(settings?.scoring_weights || {});
  const [bands, setBands] = useState(settings?.grade_bands || {});
  const [lock, setLock] = useState(settings?.edit_lock_days ?? 7);
  useEffect(() => { setWeights(settings?.scoring_weights || {}); setBands(settings?.grade_bands || {}); setLock(settings?.edit_lock_days ?? 7); }, [settings]);
  const total = Object.values(weights).reduce((a, b) => a + (Number(b) || 0), 0);
  const save = async () => {
    if (total !== 100) { toast.error(`Weights must total 100 (currently ${total})`); return; }
    const updated = await api.updateSettings({ ...settings, scoring_weights: weights, grade_bands: bands, edit_lock_days: lock });
    setSettings(updated); toast.success("Scoring settings saved");
  };
  const WL = { revenue: "Revenue", visits: "Visits", calls: "Calls", connect_rate: "Connect Rate", treatment_conversion: "Treatment Conversion" };
  return (
    <div className="space-y-5">
      <div className="surface p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading text-lg font-light text-white">Scoring Weights</div>
          <span className={`text-sm font-mono font-bold ${total === 100 ? "text-emerald-400" : "text-rose-400"}`}>Total: {total}/100</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
          {Object.keys(WL).map((k) => (
            <div key={k}><label className="text-xs font-semibold text-slate-400">{WL[k]}</label>
              <Input type="number" value={weights[k] ?? ""} onChange={(e) => setWeights({ ...weights, [k]: parseInt(e.target.value || "0", 10) })} className="rounded-lg font-mono mt-1" data-testid={`weight-${k}`} /></div>
          ))}
        </div>
      </div>
      <div className="surface p-5">
        <div className="font-heading text-lg font-light text-white mb-4">Grade Bands (min score)</div>
        <div className="grid grid-cols-3 gap-3">
          {["A", "B", "C"].map((g) => (
            <div key={g}><label className="text-xs font-semibold text-slate-400">Grade {g} ≥</label>
              <Input type="number" value={bands[g] ?? ""} onChange={(e) => setBands({ ...bands, [g]: parseInt(e.target.value || "0", 10) })} className="rounded-lg font-mono mt-1" data-testid={`band-${g}`} /></div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-2">Grade D is anything below Grade C.</p>
      </div>
      <div className="surface p-5">
        <div className="font-heading text-lg font-light text-white mb-3 flex items-center gap-2"><Lock className="h-4 w-4" /> Entry Edit-Lock Window</div>
        <div className="flex items-center gap-2 text-sm flex-wrap">
          <span className="text-slate-400">Telecallers can edit their entries for</span>
          <Input type="number" value={lock} onChange={(e) => setLock(parseInt(e.target.value || "0", 10))} className="rounded-lg font-mono w-20 h-9" data-testid="lock-days" />
          <span className="text-slate-400">days (Owner/Manager always).</span>
        </div>
      </div>
      <Button onClick={save} className={`rounded-lg ${btnGold}`} data-testid="save-scoring-btn">Save</Button>
    </div>
  );
}

function ListsTab() {
  const { settings, setSettings } = useApp();
  const [reasons, setReasons] = useState(settings?.no_treatment_reasons || []);
  const [sources, setSources] = useState(settings?.referral_sources || []);
  const [statuses, setStatuses] = useState(settings?.attendance_statuses || []);
  useEffect(() => { setReasons(settings?.no_treatment_reasons || []); setSources(settings?.referral_sources || []); setStatuses(settings?.attendance_statuses || []); }, [settings]);
  const save = async () => {
    const updated = await api.updateSettings({ ...settings, no_treatment_reasons: reasons, referral_sources: sources, attendance_statuses: statuses });
    setSettings(updated); toast.success("Lists saved");
  };
  return (
    <div className="space-y-5">
      <ListEditor label="No-Treatment Reasons" items={reasons} onChange={setReasons} testid="reasons" />
      <ListEditor label="Referral Sources" items={sources} onChange={setSources} testid="sources" />
      <ListEditor label="Attendance Statuses" items={statuses} onChange={setStatuses} testid="statuses" />
      <Button onClick={save} className={`rounded-lg ${btnGold}`} data-testid="save-lists-btn">Save Lists</Button>
    </div>
  );
}

export default function Settings() {
  return (
    <div className="animate-fade-up">
      <SectionTitle icon={SettingsIcon} title="Settings & Targets" desc="Owner control center for your team" />
      <Tabs defaultValue="employees">
        <TabsList className="bg-white/5 rounded-xl p-1 mb-5 flex-wrap h-auto">
          <TabsTrigger value="employees" data-testid="set-tab-employees" className="rounded-lg data-[state=active]:bg-white/10 data-[state=active]:text-white"><Users className="h-4 w-4 mr-1.5" /> Employees</TabsTrigger>
          <TabsTrigger value="targets" data-testid="set-tab-targets" className="rounded-lg data-[state=active]:bg-white/10 data-[state=active]:text-white"><SlidersHorizontal className="h-4 w-4 mr-1.5" /> Targets</TabsTrigger>
          <TabsTrigger value="kpis" data-testid="set-tab-kpis" className="rounded-lg data-[state=active]:bg-white/10 data-[state=active]:text-white"><ListChecks className="h-4 w-4 mr-1.5" /> Custom KPIs</TabsTrigger>
          <TabsTrigger value="scoring" data-testid="set-tab-scoring" className="rounded-lg data-[state=active]:bg-white/10 data-[state=active]:text-white"><Scale className="h-4 w-4 mr-1.5" /> Scoring</TabsTrigger>
          <TabsTrigger value="lists" data-testid="set-tab-lists" className="rounded-lg data-[state=active]:bg-white/10 data-[state=active]:text-white"><ListChecks className="h-4 w-4 mr-1.5" /> Dropdowns</TabsTrigger>
        </TabsList>
        <TabsContent value="employees"><EmployeesTab /></TabsContent>
        <TabsContent value="targets"><TargetsTab /></TabsContent>
        <TabsContent value="kpis"><CustomKpiTab /></TabsContent>
        <TabsContent value="scoring"><ScoringTab /></TabsContent>
        <TabsContent value="lists"><ListsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
